import { fetchJSON as fetchAPI } from './client';

export type DigitalEmployee = {
  id: string;
  tenant_id: string;
  type: string;
  name: string;
  mission: string;
  responsibilities: string[];
  skills: string[];
  knowledge_sources: string[];
  authority_limits: string;
  approval_requirements: string;
  escalation_rules: string;
  kpis: string[];
  status: string;
  productivity_score: number;
  health_score: number;
  active_tasks: number;
};

export type PlanningGoal = {
  id: string;
  tenant_id: string;
  goal_type: string;
  title: string;
  description: string;
  target_metric: string;
  target_value: number;
  current_value: number;
  priority: string;
  status: string;
  assigned_agents: string[];
  progress_pct: number;
  requires_approval: boolean;
  created_at: string;
  updated_at: string;
};

export type PlanningTask = {
  id: string;
  tenant_id: string;
  goal_id: string;
  title: string;
  description: string;
  assigned_employee: string;
  status: string;
  collaboration_mode: string;
  depends_on: string[];
  requires_approval: boolean;
  approval_status: string;
  reasoning_summary: string;
  confidence: number;
  created_at: string;
  updated_at: string;
};

export type AgentAssignment = {
  id: string;
  tenant_id: string;
  task_id: string;
  goal_id: string;
  employee_type: string;
  employee_name: string;
  role: string;
  status: string;
  assigned_at: string;
};

export type ExecutionReadiness = {
  tenant_id: string;
  overall_score: number;
  ready: boolean;
  data_completeness: number;
  constraint_compliance: number;
  policy_compliance: number;
  budget_available: boolean;
  capacity_available: boolean;
  risk_within_threshold: boolean;
  approval_status: string;
  integration_health: number;
  blockers: string[];
  validated_at: string;
};

export type LearningRecord = {
  id: string;
  tenant_id: string;
  employee_type: string;
  task_id?: string;
  feedback_type: string;
  agent_success: boolean;
  recommendation_accuracy: number;
  approval_rate: number;
  business_outcome?: string;
  planner_feedback?: string;
  executive_feedback?: string;
  improvement_action?: string;
  created_at: string;
};

export type AgentPerformance = {
  employee_type: string;
  employee_name: string;
  tasks_completed: number;
  success_rate: number;
  avg_confidence: number;
  approval_rate: number;
  productivity_score: number;
  health_score: number;
};

export type CollaborationGraph = {
  tenant_id: string;
  nodes: { id: string; label: string; node_type: string; employee_type: string; status: string }[];
  edges: { from: string; to: string; relation: string; mode: string }[];
};

export type AutonomousDashboard = {
  tenant_id: string;
  active_digital_employees: number;
  active_goals: number;
  pending_tasks: number;
  pending_approvals: number;
  autonomous_planning_rate: number;
  agent_productivity: number;
  avg_approval_time_hours: number;
  execution_readiness_score: number;
  planning_cycle_reduction_pct: number;
  recommendation_accuracy: number;
  business_outcome_score: number;
  learning_improvement: number;
  agent_health_score: number;
  planning_roi: number;
  planning_autonomy_score: number;
  workforce_utilization: number;
  digital_employee_performance_index: number;
  planning_automation_score: number;
};

export type AutonomousPlanningSummary = {
  tenant_id: string;
  goals: PlanningGoal[];
  tasks: PlanningTask[];
  assignments: AgentAssignment[];
  active_cycle: string;
  cycle_status: string;
  human_governance_enabled: boolean;
  emergency_stop: boolean;
  last_cycle_at: string;
};

const API_BASE = import.meta.env.VITE_AUTONOMOUSPLANNING_API ?? '/api/v1';

function apFetch<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(API_BASE, path, init);
}

export const autonomousPlanningApi = {
  summary: () => apFetch<AutonomousPlanningSummary>('/autonomous-planning'),
  dashboard: () => apFetch<AutonomousDashboard>('/autonomous-dashboard'),
  listDigitalEmployees: () => apFetch<DigitalEmployee[]>('/digital-employees'),
  createGoal: (body: { goal_type: string; title: string; description?: string; target_metric: string; target_value: number; priority?: string }) =>
    apFetch<PlanningGoal>('/autonomous-planning/goals', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }),
  assignAgent: (body: { task_id: string; goal_id: string; employee_type: string; role?: string }) =>
    apFetch<AgentAssignment>('/agent-assignments', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }),
  executionReadiness: () => apFetch<ExecutionReadiness>('/execution-readiness'),
  processApproval: (body: { task_id?: string; goal_id?: string; action: string; comment?: string; reason?: string }) =>
    apFetch<{ id: string; status: string }>('/approvals', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }),
  learning: () => apFetch<LearningRecord[]>('/learning'),
  collaborationGraph: () => apFetch<CollaborationGraph>('/autonomous-planning/collaboration-graph'),
  agentPerformance: () => apFetch<AgentPerformance[]>('/autonomous-planning/agent-performance'),
};
